import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Search, Bell, UserPlus } from 'lucide-react'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { logout } from '../../lib/auth'

export function TopBar() {
  const { user, loading, isAdmin } = useCurrentUser()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  return (
    <header className="sticky top-0 z-30 h-14 bg-white border-b border-light-border flex items-center justify-between px-4">
      {/* Left — Logo */}
      <Link to="/projects" className="shrink-0">
        <img src="/logo-orion.png" alt="Orion SEO" className="h-10" />
      </Link>

      {/* Center — Search (inactive) */}
      <div className="flex-1 flex justify-center px-6">
        <div className="flex items-center gap-2 bg-light-surface-hover rounded-lg px-3 py-1.5 text-sm text-[#8B8FA7] max-w-md w-full opacity-50 cursor-not-allowed select-none">
          <Search size={16} className="shrink-0" />
          <span>Rechercher un domaine, un mot-cle...</span>
        </div>
      </div>

      {/* Right — Actions + Profile */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Notifications (inactive) */}
        <button
          className="p-1 text-[#8B8FA7] opacity-40 cursor-not-allowed"
          title="Notifications (bientot)"
          disabled
        >
          <Bell size={20} />
        </button>

        {/* Invite (inactive) */}
        <button
          className="flex items-center gap-1.5 text-sm text-[#8B8FA7] opacity-40 cursor-not-allowed border border-light-border rounded-lg px-3 py-1"
          disabled
        >
          <UserPlus size={14} />
          Inviter
        </button>

        {/* Profile */}
        <div className="relative" ref={dropdownRef}>
          {loading ? (
            <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
          ) : (
            <button
              onClick={() => setDropdownOpen(prev => !prev)}
              className="focus:outline-none"
              aria-label="Menu profil"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center text-white text-sm font-medium">
                  {user?.name?.[0]?.toUpperCase() ?? '?'}
                </div>
              )}
            </button>
          )}

          {/* Dropdown */}
          {dropdownOpen && user && (
            <div className="absolute right-0 top-full mt-2 min-w-[220px] bg-white border border-light-border rounded-lg shadow-lg z-50">
              <div className="px-4 py-3">
                <p className="font-medium text-[#1B2559] text-sm truncate">{user.name}</p>
                <p className="text-sm text-[#8B8FA7] truncate">{user.email}</p>
              </div>
              <hr className="border-light-border" />
              <div className="py-1">
                <p className="px-4 py-1 text-[11px] uppercase tracking-wider text-[#8B8FA7] font-medium">Mon compte</p>
                <Link
                  to="/settings/google-account"
                  onClick={() => setDropdownOpen(false)}
                  className="block px-4 py-2 text-sm text-[#1B2559] hover:bg-light-surface-hover transition-colors"
                >
                  Connexion Google
                </Link>
              </div>
              {isAdmin && (
                <>
                  <hr className="border-light-border" />
                  <div className="py-1">
                    <p className="px-4 py-1 text-[11px] uppercase tracking-wider text-[#8B8FA7] font-medium">Administration</p>
                    <Link
                      to="/costs"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-[#1B2559] hover:bg-light-surface-hover transition-colors"
                    >
                      Co&#251;ts API
                    </Link>
                    <Link
                      to="/admin/global-database"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-[#1B2559] hover:bg-light-surface-hover transition-colors"
                    >
                      Global Database
                    </Link>
                    <Link
                      to="/admin/users"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-[#1B2559] hover:bg-light-surface-hover transition-colors"
                    >
                      Utilisateurs
                    </Link>
                  </div>
                </>
              )}
              <hr className="border-light-border" />
              <div className="py-1">
                <button
                  onClick={logout}
                  className="w-full text-left px-4 py-2 text-sm text-[#1B2559] hover:bg-light-surface-hover transition-colors"
                >
                  Se d&#233;connecter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
